import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqCategories = [
  {
    title: "Paver Patios & Walkways",
    faqs: [
      {
        question: "How much does a paver patio cost in North NJ?",
        answer: "Paver patio costs depend on size, materials, and site conditions. Most paver patios in North Jersey run about $20 to $35 per square foot installed. As a rough guide, a small patio typically runs $7,500 to $9,800, an average patio $15,000 to $20,000, and a large patio $25,000 to $50,000 or more. These are estimated ranges, not a quote. We give you an itemized estimate after seeing the space so you know exactly what you're paying for."
      },
      {
        question: "How long does paver patio installation take?",
        answer: "Most paver patios take 3-5 days once materials arrive. Larger projects or those requiring significant excavation may take longer. We'll give you a realistic timeline at your estimate."
      },
      {
        question: "Why do paver patios sink?",
        answer: "Pavers sink when the base underneath fails. This usually happens because the base wasn't deep enough, wasn't compacted properly, or doesn't drain well. Water trapped under pavers accelerates settling, especially through freeze-thaw cycles."
      },
      {
        question: "Can you fix my existing patio instead of replacing it?",
        answer: "Sometimes. If the base is sound and only a section has settled, we can lift and reset pavers. But if the base or drainage is the problem, surface fixes just fail again. We'll give you an honest assessment."
      },
    ]
  },
  {
    title: "Retaining Walls",
    faqs: [
      {
        question: "How much does a retaining wall cost?",
        answer: "Retaining wall costs vary based on height, length, materials, and site conditions. Small walls (under 3 feet) typically run $30-$50 per square face foot. Taller walls requiring engineering and reinforcement cost more. We provide detailed quotes after a site visit."
      },
      {
        question: "Can you fix a leaning wall without tearing it down?",
        answer: "Sometimes. If the wall hasn't moved too far and the blocks aren't damaged, we can excavate behind it, install proper drainage, and reinforce it. But if it's too far gone, rebuilding is safer and more cost-effective long-term."
      },
      {
        question: "Do I need a permit for a retaining wall?",
        answer: "In most NJ municipalities, walls over 4 feet require a permit and sometimes engineering. We handle the permit process and know what each town requires."
      },
    ]
  },
  {
    title: "Steps & Stoops",
    faqs: [
      {
        question: "Can you repair my existing steps or do they need replacement?",
        answer: "It depends on what's failing. If the mortar joints are crumbling but the structure is sound, tuckpointing can extend their life. If the foundation has shifted or there's extensive damage, replacement is usually more cost-effective and safer."
      },
      {
        question: "How long do masonry steps last?",
        answer: "Properly built masonry steps with adequate footings and waterproofing can last 30+ years in NJ. Steps that fail early usually have foundation problems or weren't protected from water intrusion."
      },
    ]
  },
  {
    title: "Outdoor Kitchens",
    faqs: [
      {
        question: "How much does an outdoor kitchen cost?",
        answer: "Outdoor kitchen costs vary widely based on size, materials, and features. A basic island with grill cutout starts around $8,000-$12,000. Full kitchens with countertops, sinks, and fire features range from $20,000-$50,000+. We'll design to your budget."
      },
      {
        question: "Do I need utilities run to the outdoor kitchen?",
        answer: "That depends on your setup. Gas grills need a gas line. Sinks need plumbing. We coordinate with licensed plumbers and electricians as needed, or work with your existing contractor."
      },
      {
        question: "How long does installation take?",
        answer: "Most outdoor kitchens take 2-4 weeks from start to finish, depending on complexity and weather. The masonry work itself is typically 1-2 weeks; countertop fabrication and utility work add time."
      },
    ]
  },
  {
    title: "Fireplaces & Fire Pits",
    faqs: [
      {
        question: "Do I need a permit for a fire pit?",
        answer: "Many NJ towns require permits for permanent fire features. Regulations vary by municipality: setback requirements, fuel type restrictions, and safety features differ. We know the codes in each town we serve and handle permits when required.",
        linkTo: "/masonry-services/fire-pits",
        linkText: "Learn more about our fireplace and fire pit services"
      },
      {
        question: "What's the difference between a fire pit and a fireplace?",
        answer: "Fire pits are open on all sides and typically lower to the ground, great for gathering around. Fireplaces have a firebox and chimney, direct smoke away from guests, and provide a focal point. Both extend your outdoor season."
      },
    ]
  },
  {
    title: "Stone Veneer",
    faqs: [
      {
        question: "What's the difference between natural and manufactured stone veneer?",
        answer: "Natural stone is quarried rock, unique pieces with authentic character but heavier and more expensive. Manufactured stone (cultured stone) is cast concrete made to look like natural stone, lighter, more consistent, and typically 30-40% less expensive."
      },
      {
        question: "How long does stone veneer last?",
        answer: "Quality stone veneer with proper installation and waterproofing lasts 25+ years. Failures usually trace back to inadequate water management or improper attachment, problems we prevent with the right details."
      },
    ]
  },
  {
    title: "Masonry Repair",
    faqs: [
      {
        question: "How do I know if my masonry needs repair or replacement?",
        answer: "Minor cracking and mortar deterioration are usually repairable. But if you see leaning walls, major structural cracks, or extensive spalling, replacement may be more cost-effective. We'll assess the damage and give you honest options."
      },
      {
        question: "Why is my masonry crumbling?",
        answer: "Water is almost always the culprit. When water enters masonry and freezes, it expands and breaks apart the material. This is accelerated by poor drainage, failed caulking, or missing caps that let water in."
      },
    ]
  },
  {
    title: "General Questions",
    faqs: [
      {
        question: "What areas do you serve?",
        answer: "We serve all of North New Jersey, including Bergen County, Passaic County, Essex County, Morris County, and surrounding areas. Our shop is in Totowa, so we're centrally located to serve the region. Our services include concrete driveways, basement waterproofing, fireplaces and fire pits, and much more."
      },
      {
        question: "Do you offer free estimates?",
        answer: "Yes, we provide free on-site estimates for all projects. We'll assess your site, discuss your goals, and provide a detailed quote with no obligation."
      },
      {
        question: "Are you licensed and insured?",
        answer: "Yes, we are fully licensed and insured. We carry general liability insurance and workers' compensation coverage. We're happy to provide certificates upon request."
      },
      {
        question: "What is your warranty?",
        answer: "We stand behind our work with a written warranty on all projects. Specific warranty terms vary by project type, and we'll explain exactly what's covered when we provide your quote."
      },
    ]
  },
];

// Generate FAQ Schema
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqCategories.flatMap(category => 
    category.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  )
};

export default function FAQ() {
  return (
    <>
      <Helmet>
        <title>Masonry & Concrete FAQ Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Common questions about masonry, paving, concrete, and waterproofing services from Masonry Pro Construction, serving Totowa and North Jersey for 35+ years." />
        <meta property="og:title" content="Masonry & Concrete FAQ Totowa, NJ | Masonry Pro Construction" />
        <meta property="og:description" content="Common questions about masonry, paving, concrete, and waterproofing services from Masonry Pro Construction, serving Totowa and North Jersey for 35+ years." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>

      <Header />
      <Breadcrumb />

      <main>
        {/* Hero Section */}
        <section className="bg-primary text-primary-foreground py-16 md:py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked Questions: Masonry Pro in Totowa, NJ Serving North Jersey
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              Answers to common questions about masonry and concrete services in North New Jersey.
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="space-y-12">
              {faqCategories.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <h2 className="text-2xl font-bold text-foreground mb-6 pb-2 border-b border-border">
                    {category.title}
                  </h2>
                  <Accordion type="single" collapsible className="space-y-2" defaultValue={categoryIndex === 0 ? "0-0" : undefined}>
                    {category.faqs.map((faq, faqIndex) => (
                      <AccordionItem 
                        key={faqIndex} 
                        value={`${categoryIndex}-${faqIndex}`}
                        className="border border-border rounded-lg px-4 bg-card"
                      >
                        <AccordionTrigger className="text-left hover:no-underline">
                          <span className="font-medium">{faq.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {faq.answer}
                          {(faq as any).linkTo && (
                            <>{" "}<Link to={(faq as any).linkTo} className="text-secondary hover:underline">{(faq as any).linkText}</Link>.</>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Still Have Questions?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
              Give us a call or text, or schedule a free estimate. We're happy to answer any questions about your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="gold" size="lg">
                Schedule Free Estimate
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <a href="tel:+19737252395" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (973) 725-2395
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
